/**
 * The Norican I2Q product mark: inquiry to quote in one glyph. A bar (the inquiry, "I"),
 * an arrow (the "2", read as "to") and a ring with a tail (the quotation, "Q").
 *
 * It is a product mark, not a corporate logo: it sits next to the Norican logo (which
 * the brand guide forbids altering, see norican-logo.tsx) and never replaces it. Colours
 * are the guide's palette only: letters in White on a Norican Blue tile or in Norican
 * Blue on a light ground, the arrow in Accent Mid Blue on dark and Accent Blue on light
 * (text only in Norican Blue, Accent Mid Blue or White, guide p.21). The tile version
 * is the same artwork as public/brand/i2q-mark.svg.
 *
 * `tone`:
 *   - `tile`    (default) the mark on its own Norican Blue tile, for any ground.
 *   - `onDark`  letters White, arrow Accent Mid Blue, no tile (the sidebar rail).
 *   - `onLight` letters Norican Blue, arrow Accent Blue, no tile (light headers).
 *   - `auto`    onLight in light mode, onDark in dark mode, following the app theme.
 *
 * `size` is the box in pixels; the artwork fills it (no built-in clear space), so
 * callers keep a gap of about a quarter of the size to neighbouring content.
 */

import { cn } from "@norican/react";

import { useAppTheme } from "@/contexts/theme";
import { PRODUCT_SHORT } from "@/lib/product";

export type I2qLogoTone = "tile" | "onDark" | "onLight" | "auto";

export interface I2qLogoProps {
  tone?: I2qLogoTone;
  /** Box size in pixels; 16 and 24 still read as three shapes. */
  size?: 16 | 20 | 24 | 28 | 32 | 40 | 48 | 64;
  className?: string;
  /** Hidden from assistive technology when a neighbouring text already names the product. */
  decorative?: boolean;
}

// Brand palette (Norican Brand Guidelines 3.0): Norican Blue, White, Accent Blue, Accent
// Mid Blue. Accent Mid Blue is not in @norican/tokens yet (ds-gap G-1), so the mark carries
// the four values itself, like the brand SVG masters do.
const NORICAN_BLUE = "#03193d";
const WHITE = "#ffffff";
const ACCENT_BLUE = "#115cd3";
const ACCENT_MID_BLUE = "#96bbf6";

const SIZE_CLASS = {
  16: "size-4",
  20: "size-5",
  24: "size-6",
  28: "size-7",
  32: "size-8",
  40: "size-10",
  48: "size-12",
  64: "size-16",
} as const;

export function I2qLogo({ tone = "tile", size = 24, className, decorative = false }: I2qLogoProps) {
  const { theme } = useAppTheme();
  const resolved: Exclude<I2qLogoTone, "auto"> =
    tone === "auto" ? (theme === "dark" ? "onDark" : "onLight") : tone;
  const letters = resolved === "onLight" ? NORICAN_BLUE : WHITE;
  const arrow = resolved === "onLight" ? ACCENT_BLUE : ACCENT_MID_BLUE;
  return (
    <svg
      viewBox="0 0 64 64"
      className={cn("shrink-0", SIZE_CLASS[size], className)}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : PRODUCT_SHORT}
      aria-hidden={decorative || undefined}
      focusable="false"
    >
      {resolved === "tile" && <rect width="64" height="64" rx="14" fill={NORICAN_BLUE} />}
      <rect x="11" y="18" width="8" height="28" rx="4" fill={letters} />
      <path
        d="M26 24 L33.5 32 L26 40"
        fill="none"
        stroke={arrow}
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="45" cy="32" r="9.5" fill="none" stroke={letters} strokeWidth="6" />
      <path
        d="M51.5 38.5 L56.5 43.5"
        fill="none"
        stroke={letters}
        strokeWidth="6"
        strokeLinecap="round"
      />
    </svg>
  );
}
